// vue(desktop)와 vue-m(mobile)이 함께 쓰는 Back Order 전용 날짜 입력 wrapper.
//
// host 계약: 아래 두 import 의 '@' 는 이 파일을 번들하는 앱의 src 로 해석된다(webpack alias, jest moduleNameMapper).
//   - '@/components/common/CustomDatePicker.vue' : 두 앱의 구현이 서로 다르다. 각 앱이 자기 picker 를 제공한다.
//     이 wrapper 는 두 picker 의 prop 합집합(disable_past, disable_future, id)을 넘기고,
//     자기가 모르는 prop 은 각 picker 가 무시한다.
//   - '@/utils/date' : 각 앱의 shim 이며 shared/utils/date.js 를 재수출한다.
// 따라서 앱을 추가할 때는 위 두 경로에 호환 가능한 모듈이 있어야 한다.
import CustomDatePicker from '@/components/common/CustomDatePicker.vue';
import { isValidOptionalMdyDate, normalizeOptionalMdyDate } from '@/utils/date';

function initialState(value) {
	const normalized = normalizeOptionalMdyDate(value);
	if (isValidOptionalMdyDate(value)) return { picker_value: normalized, raw_value: null };
	return { picker_value: null, raw_value: String(value) };
}

export default {
	name: 'backOrderDatePicker',
	inheritAttrs: false,
	props: ['value', 'disabled', 'disable_past', 'disable_future', 'id'],
	data() {
		const state = initialState(this.value);
		return {
			picker_value: state.picker_value,
			picker_key: 0,
			picker_dirty: false,
			raw_value: state.raw_value,
			typing: false,
			finish_pending: false,
			selecting_calendar: false,
		};
	},
	watch: {
		value(value) {
			if (!this.typing) this.syncFromValue(value);
		},
	},
	methods: {
		isDateInputEvent(event) {
			return event && event.target && event.target.tagName === 'INPUT' && event.target.type === 'text';
		},
		captureTypedInput(event) {
			if (!this.isDateInputEvent(event)) return;
			// Typing supersedes any calendar click that never produced a picker input.
			this.selecting_calendar = false;
			this.typing = true;
			this.raw_value = event.target.value;
			this.$nextTick(this.restoreRawInput);
		},
		captureCalendarSelection(event) {
			const target = event && event.target;
			if (!target || !target.closest) return;
			const day = target.closest('.vdp-datepicker__calendar .cell.day');
			if (!day || day.classList.contains('blank') || day.classList.contains('disabled')) return;
			// The browser runs a microtask checkpoint between this capture-phase listener
			// and the day cell's own click handler, so a $nextTick reset here would clear
			// the flag before handlePickerInput reads it. handlePickerInput consumes it
			// instead, and captureTypedInput clears any flag left by a click that emitted
			// nothing.
			this.selecting_calendar = true;
		},
		commitTypedInput(event) {
			if (!this.isDateInputEvent(event) || !this.typing) return;
			this.raw_value = event.target.value;
			this.$emit('input', normalizeOptionalMdyDate(this.raw_value));
			this.finishTyping();
		},
		finishTyping() {
			if (this.finish_pending) return;
			this.finish_pending = true;
			this.$nextTick(() => {
				this.finish_pending = false;
				this.typing = false;
				this.syncFromValue(this.value);
			});
		},
		handlePickerInput(value) {
			if (this.selecting_calendar) {
				this.selecting_calendar = false;
				this.typing = false;
				this.picker_dirty = false;
				this.raw_value = null;
				this.picker_value = value;
				this.$emit('input', value);
				return;
			}
			// vuejs-datepicker parses typeable text with Date.parse before emitting. While
			// typing, keep the native text so Back Order's strict guard sees the exact input.
			if (this.typing) {
				this.picker_dirty = true;
				this.$nextTick(this.restoreRawInput);
				return;
			}
			this.picker_dirty = false;
			this.raw_value = null;
			this.picker_value = value;
			this.$emit('input', value);
		},
		syncFromValue(value) {
			const reset_picker = this.picker_dirty || this.raw_value != null;
			const normalized = normalizeOptionalMdyDate(value);
			if (isValidOptionalMdyDate(value)) {
				this.raw_value = null;
				this.picker_value = normalized;
				this.picker_dirty = false;
				if (reset_picker) this.picker_key++;
				return;
			}
			this.raw_value = String(value);
			this.picker_value = null;
			this.picker_dirty = false;
			if (reset_picker) this.picker_key++;
			this.$nextTick(this.restoreRawInput);
		},
		restoreRawInput() {
			if (this.raw_value == null || !this.$el) return;
			const input = this.$el.tagName === 'INPUT' ? this.$el : this.$el.querySelector('input');
			if (input && input.value !== this.raw_value) input.value = this.raw_value;
		},
	},
	mounted() {
		this.restoreRawInput();
	},
	updated() {
		this.restoreRawInput();
	},
	render(createElement) {
		return createElement(CustomDatePicker, {
			key: this.picker_key,
			ref: 'picker',
			props: {
				value: this.picker_value,
				disabled: this.disabled,
				disable_past: this.disable_past,
				disable_future: this.disable_future,
				id: this.id,
			},
			on: {
				input: this.handlePickerInput,
			},
			nativeOn: {
				'!input': this.captureTypedInput,
				'!keyup': this.captureTypedInput,
				'!blur': this.commitTypedInput,
				'!click': this.captureCalendarSelection,
			},
		});
	},
};
